import React from "react";
import { useTranslation } from "react-i18next";

import { IProduct, IVariant } from "redux/reducers/models/product";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import { handleChangeEventInput } from "helpers/Helpers";

import BookingFeeProductSearch from "./BookingFeeProductSearch";
import BookingFeeCustomerTypesSection from "./BookingFeeCustomerTypesSection";
import { TBookingFeeFormValues } from "../bookingFeeForm";
import { TCustomerType } from "redux/reducers/models/facility";

type TBookingFeeFormFieldsProps = {
  values: TBookingFeeFormValues;
  onChange: React.Dispatch<React.SetStateAction<TBookingFeeFormValues>>;
  productSearchEnabled?: boolean;
  hideCustomerTypesSection?: boolean;
  selectedCustomerTypes?: TCustomerType[];
  onSelectCustomerTypes?: () => void;
  onRemoveCustomerType?: (id: number) => void;
  onCreateProduct?: () => void;
};

export default function BookingFeeFormFields(props: TBookingFeeFormFieldsProps) {
  const { t } = useTranslation();
  const {
    values,
    onChange,
    productSearchEnabled = true,
    hideCustomerTypesSection = false,
    selectedCustomerTypes = [],
    onSelectCustomerTypes,
    onRemoveCustomerType,
    onCreateProduct,
  } = props;

  function handleProductSelect(product: IProduct, variant: IVariant) {
    onChange(prev => ({ ...prev, product, variant }));
  }

  function handleProductClear() {
    onChange(prev => ({ ...prev, product: null, variant: null }));
  }

  return (
    <div className="booking-fees">
      <BookingFeeProductSearch
        product={values.product}
        variant={values.variant}
        enabled={productSearchEnabled}
        onSelect={handleProductSelect}
        onClear={handleProductClear}
        onCreateProduct={onCreateProduct}
      />

      <hr />

      <div className="booking-fees__section">
        <div className="booking-fees__section-label">{t("Booking window")}</div>
        <FormLayout>
          <FormLayout.Group>
            <Input
              id="days_in_advance_start"
              value={values.days_in_advance_start}
              onChange={event => handleChangeEventInput(event, values, onChange)}
              label={t("Applies from")}
              tooltipText={t("How many days in advance does the booking fee apply?")}
              tooltipClassName="booking-fees__tooltip"
              type="number"
              suffix={t("days")}
              placeholder={t("E.g. 1")}
            />
            <Input
              id="days_in_advance_end"
              value={values.days_in_advance_end}
              onChange={event => handleChangeEventInput(event, values, onChange)}
              label={t("Applies until")}
              tooltipText={t("How many days in advance does the booking fee applied end?")}
              tooltipClassName="booking-fees__tooltip"
              type="number"
              suffix={t("days")}
              placeholder={t("E.g. 1")}
            />
          </FormLayout.Group>
        </FormLayout>
      </div>

      <hr />

      <div className="booking-fees__section booking-fees__toggle">
        <Toggle
          id="refundable"
          labelRight={t("Mark as refundable")}
          labelSupport={t("Golfers who cancel the booking will receive a refund for this fee.")}
          checked={!values.non_refundable}
          onChange={event =>
            onChange(prev => ({
              ...prev,
              non_refundable: !event.target.checked,
            }))
          }
        />
      </div>

      {!hideCustomerTypesSection ? (
        <>
          <hr />

          <BookingFeeCustomerTypesSection
            selectedTypes={selectedCustomerTypes}
            onSelectCustomerTypes={onSelectCustomerTypes ?? (() => undefined)}
            onRemoveCustomerType={onRemoveCustomerType}
          />
        </>
      ) : null}
    </div>
  );
}
